/**
 * Fusione delle fonti dati per la creazione guidata di un'azienda dal pannello email.
 * Precedenza: VIES > sito > firma > dominio.
 *
 * Ogni campo porta con se` la provenienza, che la UI mostra all'utente: un dato
 * ricavato in automatico deve essere riconoscibile da uno inserito a mano.
 */
import type { ParsedSignature } from './signatureParser';
import type { MailMessage } from '../stores/mail/types';

export type FieldSource = 'vies' | 'website' | 'signature' | 'domain' | 'manual';

export interface ProspectField<T = string> {
  value: T;
  source: FieldSource;
}

export interface CompanyDraft {
  business_name?: ProspectField;
  vat_number?: ProspectField;
  pec?: ProspectField;
  website?: ProspectField;
  phone?: ProspectField;
  email?: ProspectField;
  street_address?: ProspectField;
  street_number?: ProspectField;
  postal_code?: ProspectField;
  city?: ProspectField;
  province?: ProspectField;
  logo_url?: ProspectField;
}

export interface RawSources {
  signature?: ParsedSignature | null;
  /** Risposta dello scraping del sito (rotta core o edge di ripiego). */
  website?: Record<string, unknown> | null;
  /** Risposta VIES. */
  vies?: Record<string, unknown> | null;
  domain?: string | null;
}

type DraftKey = keyof CompanyDraft;

function clean(v: unknown): string | null {
  if (typeof v !== 'string') return null;
  const t = v.trim();
  return t.length > 0 ? t : null;
}

export function mergeCompanyDraft(sources: RawSources, touched?: Set<string>): CompanyDraft {
  const draft: CompanyDraft = {};

  const put = (key: DraftKey, raw: unknown, source: FieldSource) => {
    if (touched?.has(key)) return;   // l'utente ha deciso: nessuna fonte lo sovrascrive
    if (draft[key]) return;          // gia` valorizzato da una fonte piu` autorevole
    const value = clean(raw);
    if (!value) return;
    draft[key] = { value, source };
  };

  const viesRaw = sources.vies as Record<string, unknown> | null | undefined;
  const vies = viesRaw && viesRaw.valid === true ? viesRaw : null;
  const site = (sources.website || null) as Record<string, unknown> | null;
  const sig = sources.signature || null;

  // 1. VIES (fonte autoritativa)
  if (vies) {
    put('business_name', vies.business_name, 'vies');
    put('street_address', vies.street_address, 'vies');
    put('street_number', vies.street_number, 'vies');
    put('postal_code', vies.postal_code, 'vies');
    put('city', vies.city, 'vies');
    put('province', vies.province, 'vies');
  }

  // 2. Sito web
  if (site) {
    put('business_name', site.business_name, 'website');
    put('vat_number', site.vat_number, 'website');
    put('pec', site.pec, 'website');
    put('phone', site.phone, 'website');
    put('email', site.email, 'website');
    put('logo_url', site.logo_url, 'website');
    put('city', site.city, 'website');
    put('postal_code', site.postal_code, 'website');
    put('street_address', site.street_address, 'website');
  }

  // 3. Firma. Il telefono dell'azienda e` il fisso: il cellulare in firma e` della
  // persona e finirebbe per essere il recapito sbagliato sulla scheda azienda.
  if (sig) {
    put('vat_number', sig.vatNumber, 'signature');
    put('website', sig.websites[0], 'signature');
    put('phone', sig.landlines?.[0] ?? sig.phones[0], 'signature');
  }

  // 4. Dominio della email
  put('website', sources.domain, 'domain');

  return draft;
}

/** Estrae i valori nudi, per passarli a createClient. */
export function draftToValues(draft: CompanyDraft): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [k, f] of Object.entries(draft)) {
    if (f && typeof f.value === 'string') out[k] = f.value;
  }
  return out;
}

// ─────────────────────────────────────────────────────────────
// Controllo anti-doppione
// ─────────────────────────────────────────────────────────────

export interface ExistingMatches {
  company: { id: string; business_name: string } | null;
  companyMatchedBy: 'domain' | 'vat' | null;
  person: { id: string; business_name: string } | null;
}

/**
 * Decide cosa creare. Senza questo controllo ogni email da un cliente gia`
 * censito genererebbe un doppione in anagrafica.
 */
export function decideCreationPlan(m: ExistingMatches): 'create_both' | 'create_person_only' | 'link_only' {
  if (!m.company) return 'create_both';
  return m.person ? 'link_only' : 'create_person_only';
}

// La ricerca sul database sta in `prospectLookup.ts`: questo modulo resta puro
// perche' i test unitari del progetto girano in ambiente node, senza Supabase.

// ─────────────────────────────────────────────────────────────
// Partecipanti del thread
// ─────────────────────────────────────────────────────────────

const AUTOMATED_PREFIXES = ['noreply', 'no-reply', 'donotreply', 'postmaster', 'mailer-daemon', 'bounce'];

/**
 * Altri partecipanti del thread appartenenti allo stesso dominio aziendale,
 * candidati a diventare referenti della stessa azienda.
 */
export function collectThreadParticipants(args: {
  messages: MailMessage[];
  domain: string | null;
  excludeEmails: string[];
}): { email: string; name: string | null }[] {
  const { messages, domain, excludeEmails } = args;
  if (!domain) return [];

  const excluded = new Set(excludeEmails.map(e => e.toLowerCase()));
  const seen = new Set<string>();
  const out: { email: string; name: string | null }[] = [];

  const consider = (email?: string | null, name?: string | null) => {
    const e = (email || '').trim().toLowerCase();
    if (!e || !e.endsWith('@' + domain)) return;
    if (excluded.has(e) || seen.has(e)) return;
    const local = e.split('@')[0];
    if (AUTOMATED_PREFIXES.some(p => local.startsWith(p))) return;
    seen.add(e);
    out.push({ email: e, name: (name || '').trim() || null });
  };

  for (const m of messages) {
    consider(m.from_email, m.from_name);
    for (const t of m.to_emails || []) consider(t.email, t.name);
    for (const c of m.cc_emails || []) consider(c.email, c.name);
  }
  return out;
}
